import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, ValidationErrors } from '@angular/forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { ODataModelChangeEvent } from '../../interfaces/odata-model.interface';
import { RatingModel } from '../../models/primitive/rating.model';

@Component({
    selector: 'pace-core-rating-input',
    templateUrl: './rating-input.component.html',
    styleUrls: ['./rating-input.component.scss'],
})
export class RatingInputComponent implements OnInit, OnDestroy {
    @Input()
    public model: RatingModel;

    @Output()
    public modelChange = new EventEmitter<RatingModel>();

    @Input()
    public parentForm: FormArray | undefined;

    @Output()
    public valueChange = new EventEmitter<ODataModelChangeEvent<RatingModel>>();

    @Input()
    public ariaLabel = 'Rating Input';

    public ratingInputControl: AbstractControl;
    public faStar = faStar;
    public farStar = farStar;

    private destroySubject = new Subject<void>();
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly formBuilder: FormBuilder,
    ) {}

    public ngOnInit(): void {
        const ratingInputValidator = (control: AbstractControl): ValidationErrors | null => this.checkValidity(control);

        this.ratingInputControl = this.formBuilder.control(
            { value: this.model.value, disabled: this.model.options.readonly },
            ratingInputValidator,
        );

        this.ratingInputControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                tap((newVal: string) => {
                    const oldValue = this.model.value;
                    this.model.value = newVal;
                    this.modelChange.emit(this.model);
                    this.valueChange.emit({
                        oldValue: oldValue,
                        newValue: this.model.value,
                    });
                }),
                takeUntil(this.destroySubject),
            )
            .subscribe();

        // The actual control needs to be added asynchronously to avoid an ExpressionChangedAfterItHasBeenCheckedError
        // See also https://stackoverflow.com/questions/45661010/dynamic-nested-reactive-form-expressionchangedafterithasbeencheckederror
        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.ratingInputControl);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'Error in RatingInputComponent -> ngOnInit -> parentForm.push: ',
                        JSON.stringify(error),
                    ),
                );
        }
    }

    public ngOnDestroy(): void {
        this.destroySubject.next();
        this.destroySubject.complete();
    }

    public checkValidity(control: AbstractControl): ValidationErrors | null {
        const errors: ValidationErrors = {};

        const controlValue = <string>control.value;

        if (this.model.options.required && !controlValue) {
            errors.required = { required: 'this input is mandatory' };
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }
}
